// ─────────────────────────────────────────────────────────────────────────────
// Security tab — per-user multi-factor authentication.
//
// Wired to GET /users (paged) + PATCH /users/:id/mfa. Unlike the other tabs
// there is no Save Changes step: every toggle saves on the spot, so this pane
// registers nothing on `saveRef` and the header's Save button stays disabled.
//
// MFA is email-code, not TOTP — enabling it needs no enrollment on the user's
// side, they simply start getting a 6-digit code at sign-in.
// ─────────────────────────────────────────────────────────────────────────────
import panes from "./panes.module.css";
import styles from "./Security.module.css";
import { useUserMfa } from "../hooks/useUserMfa";
import { userName, userInitials } from "../../../services/usersService";
import { SectionTitle, SectionSub, Switch, LoadingPane, ErrorBanner } from "./fields";

export default function Security({ readOnly = false }) {
  const {
    users, total, loading, error, pendingId,
    search, setSearch, page, totalPages, setPage, setMfa,
  } = useUserMfa();

  return (
    <div className={`${panes.pane} ${panes.paneWide}`}>
      <SectionTitle>Multi-Factor Authentication</SectionTitle>
      <SectionSub>
        When enabled, the user is emailed a 6-digit code at every sign-in. No setup
        is required on their side, and the change applies from their next login —
        sessions already open are not interrupted.
      </SectionSub>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <input
        className={styles.search}
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by name or email…"
        aria-label="Search users"
      />

      {loading ? <LoadingPane/> : users.length === 0 ? (
        <p className={styles.empty}>
          {search ? `No users match “${search}”.` : "No users in this workspace yet."}
        </p>
      ) : (
        <div className={styles.table}>
          <div className={styles.head}>
            <span className={styles.col}>User</span>
            <span className={styles.col}>Role</span>
            <span className={`${styles.col} ${styles.colRight}`}>MFA</span>
          </div>

          {users.map((u) => (
            <div key={u.id} className={styles.row}>
              <div className={styles.user}>
                <span className={styles.avatar}>{userInitials(u)}</span>
                <span className={styles.userText}>
                  <span className={styles.name}>{userName(u)}</span>
                  <span className={styles.email}>{u.email}</span>
                </span>
              </div>

              <span className={styles.role}>{u.role}</span>

              <div className={styles.toggleCell}>
                <span className={`${styles.state} ${u.mfaEnabled ? styles.stateOn : ""}`}>
                  {u.mfaEnabled ? "On" : "Off"}
                </span>
                <Switch
                  label={`Multi-factor authentication for ${u.email}`}
                  checked={!!u.mfaEnabled}
                  onChange={(next) => setMfa(u.id, next)}
                  disabled={readOnly || pendingId === u.id}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className={styles.pager}>
          <button
            className={styles.pageBtn}
            onClick={() => setPage(page - 1)}
            disabled={page <= 1 || loading}
          >
            ← Prev
          </button>
          <span className={styles.pageInfo}>
            Page {page} of {totalPages} · {total} users
          </span>
          <button
            className={styles.pageBtn}
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages || loading}
          >
            Next →
          </button>
        </div>
      )}

      {readOnly && (
        <p className={styles.readOnlyNote}>
          You have read-only access to user management — ask an Admin to change these.
        </p>
      )}
    </div>
  );
}
